import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  Lead,
  LeadNote,
  LeadActivity,
  LeadContact,
  LeadAttachment,
  LeadFilters,
  LeadStatus,
  CountryStats,
} from '@/types/lead'

// Minimal fields for map markers
const MAP_MARKER_FIELDS = `
  id,
  name,
  status,
  country,
  country_code,
  city,
  latitude,
  longitude,
  revenue,
  revenue_range,
  employee_range,
  type,
  image_url,
  market_share,
  services,
  long_term
`

// Fields for list view cards
const LIST_VIEW_FIELDS = `
  id,
  name,
  slug,
  type,
  status,
  country,
  country_code,
  city,
  latitude,
  longitude,
  image_url,
  market_share,
  revenue,
  services,
  industry,
  employee_range,
  revenue_range,
  sailing_area,
  is_headquarter,
  long_term
`

// Bounds type for map viewport
export interface MapBounds {
  north: number
  south: number
  east: number
  west: number
}

// Data source options
export type DataSource = 'trusteddocks' | 'other' | 'all'

// Extended filters with bounds
export interface LeadMapFilters extends LeadFilters {
  bounds?: MapBounds
  limit?: number
  employeeRange?: string
  revenueRange?: string
  dataSource?: DataSource
}

// Fetch ALL leads with coordinates once - client-side filtering for bounds
export function useAllMapLeads(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['leads', 'all-map'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select(MAP_MARKER_FIELDS)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .limit(50000)

      if (error) throw error
      return (data || []) as Lead[]
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - data doesn't change often
    enabled: options?.enabled ?? true,
  })
}

// Legacy - kept for backwards compatibility
export function useLeadsInBounds(_filters?: LeadMapFilters) {
  return useAllMapLeads()
}

// Fetch leads for list view with server-side filters and pagination
export function useLeadsList(filters?: LeadMapFilters & { page?: number; pageSize?: number }) {
  const page = filters?.page || 0
  const pageSize = filters?.pageSize || 200

  return useQuery({
    queryKey: ['leads', 'list', filters, page],
    queryFn: async () => {
      let query = supabase
        .from('leads')
        .select(LIST_VIEW_FIELDS, { count: 'exact' })

      // Apply filters server-side
      if (filters?.status?.length) {
        query = query.in('status', filters.status)
      }
      if (filters?.type?.length) {
        query = query.in('type', filters.type)
      }
      if (filters?.country?.length) {
        // country filter uses country name (e.g., "Belgium", "Netherlands")
        query = query.in('country', filters.country)
      }
      if (filters?.search) {
        query = query.ilike('name', `%${filters.search}%`)
      }
      if (filters?.employeeRange) {
        query = query.eq('employee_range', filters.employeeRange)
      }
      if (filters?.revenueRange) {
        query = query.eq('revenue_range', filters.revenueRange)
      }
      if (filters?.dataSource && filters.dataSource !== 'all') {
        if (filters.dataSource === 'trusteddocks') {
          query = query.ilike('source_url', '%trusteddocks%')
        } else {
          // 'other' - records without trusteddocks in source_url (null or different value)
          query = query.or('source_url.is.null,source_url.not.ilike.%trusteddocks%')
        }
      }

      // Pagination
      query = query
        .order('name')
        .range(page * pageSize, (page + 1) * pageSize - 1)

      const { data, error, count } = await query

      if (error) throw error
      return {
        leads: (data || []) as Lead[],
        totalCount: count || 0,
        page,
        pageSize,
      }
    },
  })
}

// Legacy function - kept for backwards compatibility but should migrate to above
export function useLeads(filters?: LeadFilters) {
  return useQuery({
    queryKey: ['leads', filters],
    queryFn: async () => {
      let query = supabase
        .from('leads')
        .select(LIST_VIEW_FIELDS)
        .limit(1000)

      if (filters?.status?.length) {
        query = query.in('status', filters.status)
      }
      if (filters?.search) {
        query = query.ilike('name', `%${filters.search}%`)
      }

      const { data, error } = await query.order('name')

      if (error) throw error
      return data as Lead[]
    },
  })
}

// Fetch single lead by ID
export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: async () => {
      if (!id) return null

      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as Lead
    },
    enabled: !!id,
  })
}

// Fetch country statistics using database function for accurate counts
export function useLeadsByCountry() {
  return useQuery({
    queryKey: ['leads', 'country-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_country_stats')

      if (error) throw error

      // Map to CountryStats format
      return (data || []).map((row: { country: string; country_code: string; count: number }) => ({
        country: row.country,
        country_code: row.country_code || '',
        count: row.count,
        total_market_share: 0,
        leads: [], // Empty - leads will be fetched separately via bounds
      })) as CountryStats[]
    },
    staleTime: 60000, // Cache for 1 minute
  })
}

// Update lead
export function useUpdateLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Lead> & { id: string }) => {
      const { data, error } = await supabase
        .from('leads')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Lead
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead', data.id] })
    },
  })
}

// Update lead status
export function useUpdateLeadStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: LeadStatus }) => {
      const { data, error } = await supabase
        .from('leads')
        .update({ status })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Lead
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead', data.id] })
    },
  })
}

// Delete a lead
export function useDeleteLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', id)

      if (error) throw error
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

// Get available filter options based on selected country
export function useAvailableFilterOptions(country?: string) {
  return useQuery({
    queryKey: ['filter-options', country],
    queryFn: async () => {
      let query = supabase
        .from('leads')
        .select('employee_range, revenue_range')

      if (country) {
        query = query.eq('country', country)
      }

      const { data, error } = await query

      if (error) throw error

      // Extract unique values
      const employeeRanges = new Set<string>()
      const revenueRanges = new Set<string>()

      data?.forEach((lead) => {
        if (lead.employee_range) employeeRanges.add(lead.employee_range)
        if (lead.revenue_range) revenueRanges.add(lead.revenue_range)
      })

      return {
        employeeRanges: Array.from(employeeRanges),
        revenueRanges: Array.from(revenueRanges),
      }
    },
  })
}

// Lead Notes
export function useLeadNotes(leadId: string | undefined) {
  return useQuery({
    queryKey: ['lead-notes', leadId],
    queryFn: async () => {
      if (!leadId) return []

      const { data, error } = await supabase
        .from('lead_notes')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as LeadNote[]
    },
    enabled: !!leadId,
  })
}

export function useAddLeadNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ lead_id, content, created_by }: { lead_id: string; content: string; created_by?: string }) => {
      const { data, error } = await supabase
        .from('lead_notes')
        .insert({ lead_id, content, created_by })
        .select()
        .single()

      if (error) throw error
      return data as LeadNote
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lead-notes', data.lead_id] })
    },
  })
}

export function useDeleteLeadNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, lead_id }: { id: string; lead_id: string }) => {
      const { error } = await supabase.from('lead_notes').delete().eq('id', id)
      if (error) throw error
      return lead_id
    },
    onSuccess: (lead_id) => {
      queryClient.invalidateQueries({ queryKey: ['lead-notes', lead_id] })
    },
  })
}

// Lead Activities
export function useLeadActivities(leadId: string | undefined) {
  return useQuery({
    queryKey: ['lead-activities', leadId],
    queryFn: async () => {
      if (!leadId) return []

      const { data, error } = await supabase
        .from('lead_activities')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as LeadActivity[]
    },
    enabled: !!leadId,
  })
}

export function useAddLeadActivity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (activity: Omit<LeadActivity, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('lead_activities')
        .insert(activity)
        .select()
        .single()

      if (error) throw error
      return data as LeadActivity
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lead-activities', data.lead_id] })
    },
  })
}

// Lead Contacts
export function useLeadContacts(leadId: string | undefined) {
  return useQuery({
    queryKey: ['lead-contacts', leadId],
    queryFn: async () => {
      if (!leadId) return []

      const { data, error } = await supabase
        .from('lead_contacts')
        .select('*')
        .eq('lead_id', leadId)
        .order('is_primary', { ascending: false })
        .order('name')

      if (error) throw error
      return data as LeadContact[]
    },
    enabled: !!leadId,
  })
}

export function useAddLeadContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (contact: Omit<LeadContact, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('lead_contacts')
        .insert(contact)
        .select()
        .single()

      if (error) throw error
      return data as LeadContact
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lead-contacts', data.lead_id] })
    },
  })
}

export function useUpdateLeadContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, lead_id, ...updates }: Partial<LeadContact> & { id: string; lead_id: string }) => {
      const { data, error } = await supabase
        .from('lead_contacts')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return { ...data, lead_id } as LeadContact
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lead-contacts', data.lead_id] })
    },
  })
}

export function useDeleteLeadContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, lead_id }: { id: string; lead_id: string }) => {
      const { error } = await supabase.from('lead_contacts').delete().eq('id', id)
      if (error) throw error
      return lead_id
    },
    onSuccess: (lead_id) => {
      queryClient.invalidateQueries({ queryKey: ['lead-contacts', lead_id] })
    },
  })
}

// Lead Attachments
export function useLeadAttachments(leadId: string | undefined) {
  return useQuery({
    queryKey: ['lead-attachments', leadId],
    queryFn: async () => {
      if (!leadId) return []

      const { data, error } = await supabase
        .from('lead_attachments')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as LeadAttachment[]
    },
    enabled: !!leadId,
  })
}

export function useUploadLeadAttachment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leadId, file }: { leadId: string; file: File }) => {
      const fileExt = file.name.split('.').pop()
      const fileName = `${leadId}/${Date.now()}.${fileExt}`

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('lead-attachments')
        .upload(fileName, file)

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('lead-attachments')
        .getPublicUrl(fileName)

      // Save attachment record
      const { data, error } = await supabase
        .from('lead_attachments')
        .insert({
          lead_id: leadId,
          file_name: file.name,
          file_path: urlData.publicUrl,
          file_size: file.size,
          file_type: file.type,
        })
        .select()
        .single()

      if (error) throw error
      return data as LeadAttachment
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lead-attachments', data.lead_id] })
    },
  })
}

export function useDeleteLeadAttachment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, lead_id, file_path }: { id: string; lead_id: string; file_path: string }) => {
      // Extract file name from URL for deletion
      const fileName = file_path.split('/lead-attachments/')[1]

      if (fileName) {
        await supabase.storage.from('lead-attachments').remove([fileName])
      }

      const { error } = await supabase.from('lead_attachments').delete().eq('id', id)
      if (error) throw error
      return lead_id
    },
    onSuccess: (lead_id) => {
      queryClient.invalidateQueries({ queryKey: ['lead-attachments', lead_id] })
    },
  })
}

// Upload lead image
export function useUploadLeadImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leadId, file }: { leadId: string; file: File }) => {
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg'
      const fileName = `shipyards/${leadId}.${fileExt}`

      // Upload to storage (bucket: lead-images, folder: shipyards/)
      const { error: uploadError } = await supabase.storage
        .from('lead-images')
        .upload(fileName, file, { upsert: true })

      if (uploadError) throw uploadError

      // Get public URL with cache-busting timestamp
      const { data: urlData } = supabase.storage
        .from('lead-images')
        .getPublicUrl(fileName)

      // Add cache-busting query param for replaced images
      const imageUrl = `${urlData.publicUrl}?v=${Date.now()}`

      // Update lead with image URL
      const { data, error } = await supabase
        .from('leads')
        .update({ image_url: imageUrl })
        .eq('id', leadId)
        .select()
        .single()

      if (error) throw error
      return data as Lead
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead', data.id] })
    },
  })
}

// Fetch all long term lead IDs for filtering
export function useLongTermLeads() {
  return useQuery({
    queryKey: ['leads', 'long-term'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('id')
        .eq('long_term', true)
        .limit(50000)

      if (error) throw error
      return new Set((data || []).map(d => d.id))
    },
    staleTime: 30000,
  })
}

// Fetch leads that have been "touched" (have notes, activities, or contacts)
export function useTouchedLeads() {
  return useQuery({
    queryKey: ['leads', 'touched'],
    queryFn: async () => {
      // Get lead IDs with notes
      const { data: notesData } = await supabase
        .from('lead_notes')
        .select('lead_id')

      // Get lead IDs with activities
      const { data: activitiesData } = await supabase
        .from('lead_activities')
        .select('lead_id')

      // Get lead IDs with contacts
      const { data: contactsData } = await supabase
        .from('lead_contacts')
        .select('lead_id')

      // Combine all unique lead IDs
      const touchedIds = new Set<string>()
      notesData?.forEach(d => touchedIds.add(d.lead_id))
      activitiesData?.forEach(d => touchedIds.add(d.lead_id))
      contactsData?.forEach(d => touchedIds.add(d.lead_id))

      return touchedIds
    },
    staleTime: 30000,
  })
}

// Toggle long term status
export function useToggleLongTerm() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, isLongTerm }: { id: string; isLongTerm: boolean }) => {
      const { data, error } = await supabase
        .from('leads')
        .update({ long_term: isLongTerm })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Lead
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead', data.id] })
    },
  })
}
